/**
 * Per-device polyline cache for saved routes.
 *
 * Route geometry (GeoJSON + distance + duration) is NEVER written to the
 * Sharetribe profile — only the waypoints are. On load we re-derive the
 * geometry, either by hitting this cache or by calling Mapbox Directions and
 * writing the result back.
 *
 * Storage:
 * - Web: `localStorage` under `STORAGE_KEY`.
 * - Cordova: same interface — `localStorage` is backed by persistent SQLite.
 *
 * Eviction: LRU once > `MAX_ENTRIES`, or once an entry is older than `TTL_MS`.
 */

export const STORAGE_KEY = 'vansite_routePlanner_polylineCache_v1';
export const MAX_ENTRIES = 20;
export const TTL_MS = 30 * 24 * 60 * 60 * 1000;

const canUseStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readAll = () => {
  if (!canUseStorage()) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeAll = (map) => {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // QuotaExceededError or disabled storage — nothing we can do usefully,
    // the cache is a pure optimisation and a fresh Directions call will
    // recover on next load.
  }
};

const isExpired = (entry, now) => {
  if (!entry || typeof entry.cachedAt !== 'number') return true;
  return now - entry.cachedAt > TTL_MS;
};

/**
 * Drop expired entries and, if still over cap, the least-recently-accessed
 * ones. Called implicitly on every read/write.
 */
export const evictOldEntries = () => {
  const map = readAll();
  const now = Date.now();
  let dirty = false;

  for (const key of Object.keys(map)) {
    if (isExpired(map[key], now)) {
      delete map[key];
      dirty = true;
    }
  }

  const entries = Object.entries(map);
  if (entries.length > MAX_ENTRIES) {
    entries.sort((a, b) => (a[1].lastAccessedAt || 0) - (b[1].lastAccessedAt || 0));
    const overflow = entries.length - MAX_ENTRIES;
    for (let i = 0; i < overflow; i += 1) {
      delete map[entries[i][0]];
    }
    dirty = true;
  }

  if (dirty) writeAll(map);
};

/**
 * @param {string} routeId
 * @returns {{ routeGeoJSON: import('geojson').LineString, totalDistanceKm: number, totalDurationMin: number } | null}
 */
export const getPolyline = (routeId) => {
  if (!routeId) return null;
  const map = readAll();
  const entry = map[routeId];
  if (!entry) return null;

  if (isExpired(entry, Date.now())) {
    delete map[routeId];
    writeAll(map);
    return null;
  }

  // Touch LRU timestamp so the entry survives the next eviction sweep.
  entry.lastAccessedAt = Date.now();
  writeAll(map);

  return {
    routeGeoJSON: entry.routeGeoJSON,
    totalDistanceKm: entry.totalDistanceKm,
    totalDurationMin: entry.totalDurationMin,
  };
};

/**
 * @param {string} routeId
 * @param {{ routeGeoJSON: import('geojson').LineString, totalDistanceKm: number, totalDurationMin: number }} payload
 */
export const setPolyline = (routeId, payload) => {
  if (!routeId || !payload || !payload.routeGeoJSON) return;
  const map = readAll();
  const now = Date.now();
  map[routeId] = {
    routeGeoJSON: payload.routeGeoJSON,
    totalDistanceKm: payload.totalDistanceKm,
    totalDurationMin: payload.totalDurationMin,
    cachedAt: now,
    lastAccessedAt: now,
  };
  writeAll(map);
  evictOldEntries();
};

export const removePolyline = (routeId) => {
  if (!routeId) return;
  const map = readAll();
  if (map[routeId]) {
    delete map[routeId];
    writeAll(map);
  }
};

export const clearAll = () => {
  if (!canUseStorage()) return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
